import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Slider,
  Paper,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface AssessmentData {
  age: number;
  gender: string;
  hasChildren: string;
  numberOfChildren: number;
  inSchool: string;
  workStatus: string;
  workHours: number;
  fitnessLevel: string;
  healthGoals: string;
  timeAvailability: string;
  sleepHours: number;
}

const HealthAssessment: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<AssessmentData>({
    age: 30,
    gender: '',
    hasChildren: '',
    numberOfChildren: 0,
    inSchool: '',
    workStatus: '',
    workHours: 40,
    fitnessLevel: '',
    healthGoals: '',
    timeAvailability: '',
    sleepHours: 7,
  });

  const steps = [
    'Personal Info',
    'Life Situation',
    'Work & Schedule',
    'Health & Goals',
    'Time Assessment'
  ];

  const handleInputChange = (field: keyof AssessmentData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateRecommendedHours = (): number => {
    let baseHours = 6; // Base recommendation (middle of 0-20 range, motivated general user level)
    
    // Fitness level (primary factor for new segmentation)
    if (formData.fitnessLevel === 'sedentary') {
      baseHours = 0.5; // Sedentary: 0-1 hours (low effort)
    } else if (formData.fitnessLevel === 'beginner') {
      baseHours = 2; // Beginners: 1-3 hours (low effort)
    } else if (formData.fitnessLevel === 'intermediate') {
      baseHours = 6; // Motivated general users: 4-7 hours
    } else if (formData.fitnessLevel === 'advanced') {
      baseHours = 10; // Consistent wellness seekers: 8-12 hours
    }
    
    // Health goals (major adjustment factor)
    if (formData.healthGoals === 'major-transformation') {
      baseHours += 8; // Push toward biohacker optimizer range (13-20 hours)
    } else if (formData.healthGoals === 'significant-improvement') {
      baseHours += 3; // Moderate increase
    } else if (formData.healthGoals === 'maintenance') {
      baseHours -= 2; // Keep it lower
    }
    
    // Time availability (constraining factor)
    if (formData.timeAvailability === 'very-limited') {
      baseHours = Math.min(baseHours, 2); // Cap at beginner level
    } else if (formData.timeAvailability === 'limited') {
      baseHours = Math.min(baseHours, 6); // Cap at motivated general user level
    } else if (formData.timeAvailability === 'flexible') {
      baseHours += 2; // Small boost for flexibility
    }
    
    // Age adjustments (smaller impact)
    if (formData.age > 65) {
      baseHours += 1; // More time for health focus
    } else if (formData.age < 25) {
      baseHours += 1; // Young people can handle more
    }
    
    // Family responsibilities (time constraint)
    if (formData.hasChildren === 'yes') {
      const reduction = Math.min(formData.numberOfChildren * 1, 3); // Max 3 hour reduction
      baseHours -= reduction;
    }
    
    // School status (time constraint)
    if (formData.inSchool === 'yes') {
      baseHours -= 2;
    }
    
    // Work status adjustments (smaller impact in new range)
    if (formData.workStatus === 'unemployed' || formData.workStatus === 'retired') {
      baseHours += 3; // More available time
    } else if (formData.workStatus === 'part-time') {
      baseHours += 1;
    } else if (formData.workHours > 50) {
      baseHours -= 2; // Less available time
    }
    
    // Sleep (affects energy for health activities)
    if (formData.sleepHours < 6) {
      baseHours -= 1; // Poor sleep = less energy
    } else if (formData.sleepHours >= 8) {
      baseHours += 1; // Good sleep = more energy
    }
    
    // Ensure reasonable bounds (0-20 hours per week)
    return Math.max(0, Math.min(20, Math.round(baseHours * 10) / 10)); // Round to 1 decimal place
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final step - calculate and redirect
      const recommendedHours = calculateRecommendedHours();
      localStorage.setItem('recommendedWeeklyHours', recommendedHours.toString());
      navigate('/talent-tree?hours=' + recommendedHours);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Personal Info
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ color: '#4caf50', mb: 2 }}>
              Let's start with some basic information
            </Typography>
            
            <Box>
              <Typography variant="body1" sx={{ mb: 2, color: 'white' }}>
                What is your age?
              </Typography>
              <Slider
                value={formData.age}
                onChange={(_, value) => handleInputChange('age', value)}
                min={18}
                max={80}
                marks={[
                  { value: 20, label: '20' },
                  { value: 40, label: '40' },
                  { value: 60, label: '60' },
                  { value: 80, label: '80' }
                ]}
                valueLabelDisplay="on"
                sx={{ color: '#4caf50' }}
              />
            </Box>

            <FormControl>
              <FormLabel sx={{ color: 'white', mb: 1 }}>Gender</FormLabel>
              <RadioGroup
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                sx={{ '& .MuiFormControlLabel-label': { color: 'white' } }}
              >
                <FormControlLabel value="male" control={<Radio sx={{ color: '#4caf50' }} />} label="Male" />
                <FormControlLabel value="female" control={<Radio sx={{ color: '#4caf50' }} />} label="Female" />
                <FormControlLabel value="other" control={<Radio sx={{ color: '#4caf50' }} />} label="Other" />
                <FormControlLabel value="prefer-not-to-say" control={<Radio sx={{ color: '#4caf50' }} />} label="Prefer not to say" />
              </RadioGroup>
            </FormControl>
          </Box>
        );

      case 1: // Life Situation
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ color: '#4caf50', mb: 2 }}>
              Tell us about your current life situation
            </Typography>
            
            <FormControl>
              <FormLabel sx={{ color: 'white', mb: 1 }}>Do you have children?</FormLabel>
              <RadioGroup
                value={formData.hasChildren}
                onChange={(e) => handleInputChange('hasChildren', e.target.value)}
                sx={{ '& .MuiFormControlLabel-label': { color: 'white' } }}
              >
                <FormControlLabel value="yes" control={<Radio sx={{ color: '#4caf50' }} />} label="Yes" />
                <FormControlLabel value="no" control={<Radio sx={{ color: '#4caf50' }} />} label="No" />
              </RadioGroup>
            </FormControl>

            {formData.hasChildren === 'yes' && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2, color: 'white' }}>
                  How many children do you have?
                </Typography>
                <Slider
                  value={formData.numberOfChildren}
                  onChange={(_, value) => handleInputChange('numberOfChildren', value)}
                  min={1}
                  max={6}
                  marks
                  valueLabelDisplay="on"
                  sx={{ color: '#4caf50' }}
                />
              </Box>
            )}

            <FormControl>
              <FormLabel sx={{ color: 'white', mb: 1 }}>Are you currently in school?</FormLabel>
              <RadioGroup
                value={formData.inSchool}
                onChange={(e) => handleInputChange('inSchool', e.target.value)}
                sx={{ '& .MuiFormControlLabel-label': { color: 'white' } }}
              >
                <FormControlLabel value="yes" control={<Radio sx={{ color: '#4caf50' }} />} label="Yes (high school, college, graduate school)" />
                <FormControlLabel value="no" control={<Radio sx={{ color: '#4caf50' }} />} label="No" />
              </RadioGroup>
            </FormControl>
          </Box>
        );

      case 2: // Work & Schedule
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ color: '#4caf50', mb: 2 }}>
              What's your work situation?
            </Typography>
            
            <FormControl>
              <FormLabel sx={{ color: 'white', mb: 1 }}>Employment Status</FormLabel>
              <RadioGroup
                value={formData.workStatus}
                onChange={(e) => handleInputChange('workStatus', e.target.value)}
                sx={{ '& .MuiFormControlLabel-label': { color: 'white' } }}
              >
                <FormControlLabel value="full-time" control={<Radio sx={{ color: '#4caf50' }} />} label="Full-time employed" />
                <FormControlLabel value="part-time" control={<Radio sx={{ color: '#4caf50' }} />} label="Part-time employed" />
                <FormControlLabel value="self-employed" control={<Radio sx={{ color: '#4caf50' }} />} label="Self-employed/Freelancer" />
                <FormControlLabel value="unemployed" control={<Radio sx={{ color: '#4caf50' }} />} label="Unemployed/Job seeking" />
                <FormControlLabel value="retired" control={<Radio sx={{ color: '#4caf50' }} />} label="Retired" />
                <FormControlLabel value="student" control={<Radio sx={{ color: '#4caf50' }} />} label="Full-time student" />
              </RadioGroup>
            </FormControl>

            {(formData.workStatus === 'full-time' || formData.workStatus === 'part-time' || formData.workStatus === 'self-employed') && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2, color: 'white' }}>
                  How many hours per week do you work?
                </Typography>
                <Slider
                  value={formData.workHours}
                  onChange={(_, value) => handleInputChange('workHours', value)}
                  min={10}
                  max={80}
                  marks={[
                    { value: 20, label: '20' },
                    { value: 40, label: '40' },
                    { value: 60, label: '60' },
                    { value: 80, label: '80+' }
                  ]}
                  valueLabelDisplay="on"
                  sx={{ color: '#4caf50' }}
                />
              </Box>
            )}

            <FormControl>
              <FormLabel sx={{ color: 'white', mb: 1 }}>How would you describe your time availability?</FormLabel>
              <RadioGroup
                value={formData.timeAvailability}
                onChange={(e) => handleInputChange('timeAvailability', e.target.value)}
                sx={{ '& .MuiFormControlLabel-label': { color: 'white' } }}
              >
                <FormControlLabel value="very-limited" control={<Radio sx={{ color: '#4caf50' }} />} label="Very limited - barely any free time" />
                <FormControlLabel value="limited" control={<Radio sx={{ color: '#4caf50' }} />} label="Limited - some free time on weekends" />
                <FormControlLabel value="moderate" control={<Radio sx={{ color: '#4caf50' }} />} label="Moderate - regular pockets of free time" />
                <FormControlLabel value="flexible" control={<Radio sx={{ color: '#4caf50' }} />} label="Flexible - good control over my schedule" />
              </RadioGroup>
            </FormControl>
          </Box>
        );

      case 3: // Health & Goals
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ color: '#4caf50', mb: 2 }}>
              Let's talk about your health and fitness
            </Typography>
            
            <FormControl>
              <FormLabel sx={{ color: 'white', mb: 1 }}>Current fitness level</FormLabel>
              <RadioGroup
                value={formData.fitnessLevel}
                onChange={(e) => handleInputChange('fitnessLevel', e.target.value)}
                sx={{ '& .MuiFormControlLabel-label': { color: 'white' } }}
              >
                <FormControlLabel value="sedentary" control={<Radio sx={{ color: '#4caf50' }} />} label="Sedentary - little to no exercise" />
                <FormControlLabel value="beginner" control={<Radio sx={{ color: '#4caf50' }} />} label="Beginner - some occasional activity" />
                <FormControlLabel value="intermediate" control={<Radio sx={{ color: '#4caf50' }} />} label="Intermediate - regular exercise routine" />
                <FormControlLabel value="advanced" control={<Radio sx={{ color: '#4caf50' }} />} label="Advanced - very active lifestyle" />
              </RadioGroup>
            </FormControl>

            <FormControl>
              <FormLabel sx={{ color: 'white', mb: 1 }}>What are your health goals?</FormLabel>
              <RadioGroup
                value={formData.healthGoals}
                onChange={(e) => handleInputChange('healthGoals', e.target.value)}
                sx={{ '& .MuiFormControlLabel-label': { color: 'white' } }}
              >
                <FormControlLabel value="maintenance" control={<Radio sx={{ color: '#4caf50' }} />} label="Maintenance - keep current health level" />
                <FormControlLabel value="gradual-improvement" control={<Radio sx={{ color: '#4caf50' }} />} label="Gradual improvement - slow steady progress" />
                <FormControlLabel value="significant-improvement" control={<Radio sx={{ color: '#4caf50' }} />} label="Significant improvement - noticeable changes" />
                <FormControlLabel value="major-transformation" control={<Radio sx={{ color: '#4caf50' }} />} label="Major transformation - complete lifestyle change" />
              </RadioGroup>
            </FormControl>

            <Box>
              <Typography variant="body1" sx={{ mb: 2, color: 'white' }}>
                How many hours of sleep do you typically get per night?
              </Typography>
              <Slider
                value={formData.sleepHours}
                onChange={(_, value) => handleInputChange('sleepHours', value)}
                min={4}
                max={10}
                marks={[
                  { value: 5, label: '5h' },
                  { value: 7, label: '7h' },
                  { value: 9, label: '9h' }
                ]}
                valueLabelDisplay="on"
                sx={{ color: '#4caf50' }}
              />
            </Box>
          </Box>
        );

      case 4: // Time Assessment
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Typography variant="h6" sx={{ color: '#4caf50', mb: 2 }}>
              Your Personalized Health Commitment
            </Typography>
            
            <Card sx={{ background: 'rgba(76, 175, 80, 0.1)', border: '1px solid #4caf50' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#4caf50', mb: 2 }}>
                  📊 Your Recommended Weekly Commitment
                </Typography>
                <Typography variant="h4" sx={{ color: 'white', mb: 1 }}>
                  {calculateRecommendedHours().toFixed(1)} hours per week
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>
                  This is our personalized recommendation based on your responses. You can always adjust this later in the talent tree.
                </Typography>
                
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                  {(() => {
                    const hours = calculateRecommendedHours();
                    if (hours <= 1) return "🌱 Sedentary Level: Starting with low-effort, sustainable habits";
                    if (hours <= 3) return "🌿 Beginner Level: Building foundational health practices";
                    if (hours <= 7) return "🏃 Motivated General User: Active wellness pursuit";
                    if (hours <= 12) return "💪 Consistent Wellness Seeker: Dedicated health optimization";
                    return "🧬 Biohacker Optimizer: Advanced longevity and performance focus";
                  })()}
                </Typography>
              </CardContent>
            </Card>

            <Box sx={{ 
              mt: 2, 
              p: 3, 
              background: 'rgba(255, 255, 255, 0.02)', 
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2 
            }}>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center' }}>
                Ready to start your health and longevity journey? Click "Complete Assessment" to apply your personalized time commitment to the talent tree.
              </Typography>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'radial-gradient(ellipse at center, #1a0033 0%, #000000 100%)',
      py: 4,
      px: 2
    }}>
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
            Time Assessment
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', mb: 4 }}>
            Help us determine your optimal weekly time commitment for health and longevity based on your current life time availability
          </Typography>
          
          {/* Progress */}
          <Box sx={{ mb: 3 }}>
            <LinearProgress 
              variant="determinate" 
              value={(currentStep + 1) / steps.length * 100}
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#4caf50'
                }
              }}
            />
            <Typography variant="body2" sx={{ color: 'white', mt: 1 }}>
              Step {currentStep + 1} of {steps.length}
            </Typography>
          </Box>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={currentStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ 
                '& .MuiStepLabel-label': { color: 'rgba(255,255,255,0.6)' },
                '& .MuiStepLabel-label.Mui-active': { color: '#4caf50' },
                '& .MuiStepLabel-label.Mui-completed': { color: 'white' }
              }}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Form Content */}
        <Paper sx={{ 
          p: 4, 
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          mb: 4
        }}>
          {renderStepContent()}
        </Paper>

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            onClick={handleBack}
            disabled={currentStep === 0}
            startIcon={<ArrowBack />}
            sx={{ 
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              '&:hover': { borderColor: 'white' }
            }}
            variant="outlined"
          >
            Back
          </Button>

          <Button
            onClick={() => navigate('/talent-tree')}
            sx={{ color: 'rgba(255,255,255,0.6)' }}
          >
            Skip Assessment
          </Button>

          <Button
            onClick={handleNext}
            endIcon={currentStep === steps.length - 1 ? null : <ArrowForward />}
            variant="contained"
            sx={{ 
              bgcolor: '#4caf50',
              '&:hover': { bgcolor: '#45a049' }
            }}
          >
            {currentStep === steps.length - 1 ? 'Complete Assessment' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default HealthAssessment; 